import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import axios from '../api/client';

// A tenant picker that searches the SERVER.
//
// THE BUG THIS FIXES
//
// Every tenant dropdown used to do `axios.get('/tenants')` and hand the result to a MUI
// Autocomplete. That endpoint pages, and its default page is fifty — so the dropdown held
// fifty tenants and Autocomplete filtered those fifty in the browser. Tenant 51 onwards
// could not be found, could not be selected, and therefore could not be invoiced or have a
// payment recorded against them. The screen looked like it was searching; it was searching
// one page.
//
// Raising the page size is NOT a fix. The endpoint caps `limit` at 100, so asking for more
// silently returns 100 — the same bug at a higher threshold, and harder to notice.
//
// So the typing goes to the server. `filterOptions={(x) => x}` is what turns Autocomplete's
// own client-side filtering off; without it the component would filter the server's answer
// a second time and re-introduce the same class of bug on top of the fix.
const TenantPicker = ({
  valueId,
  onChange,
  label = 'Tenant',
  required = false,
  disabled = false,
  helperText,
  error = false,
  getOptionLabel
}) => {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const defaultLabel = (t) => {
    const where = t.property?.propertyName || t.unit?.property?.propertyName;
    const unit = t.unit?.unitNumber ? ` Unit ${t.unit.unitNumber}` : '';
    return `${t.name}${where ? ` — ${where}${unit}` : ''}`;
  };

  const search = useCallback(async (term) => {
    setLoading(true);
    try {
      const { data } = await axios.get('/tenants', {
        params: { limit: 50, ...(term ? { search: term } : {}) }
      });
      setOptions(Array.isArray(data) ? data : (data.data || []));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced: a request per keystroke races, and the answer for "Ach" can arrive after
  // the answer for "Achieng" and overwrite it with the wider list.
  useEffect(() => {
    const timer = setTimeout(() => search(input), 300);
    return () => clearTimeout(timer);
  }, [input, search]);

  // A tenant chosen earlier — on an edit, say — will usually not be in whatever page the
  // search last returned, and Autocomplete renders a value it cannot find as blank. Fetched
  // by id so the field shows who is actually selected rather than looking empty.
  useEffect(() => {
    if (!valueId) { setSelected(null); return; }
    if (selected && (selected._id || selected.id) === valueId) return;

    const known = options.find((t) => (t._id || t.id) === valueId);
    if (known) { setSelected(known); return; }

    let cancelled = false;
    axios.get(`/tenants/${valueId}`)
      .then((res) => { if (!cancelled) setSelected(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueId, options]);

  return (
    <Autocomplete
      options={options}
      value={selected}
      loading={loading}
      disabled={disabled}
      // Server-side search: do not filter the answer again in the browser.
      filterOptions={(x) => x}
      getOptionLabel={getOptionLabel || defaultLabel}
      isOptionEqualToValue={(o, v) => (o._id || o.id) === (v._id || v.id)}
      onInputChange={(_e, val, reason) => { if (reason === 'input') setInput(val); }}
      onChange={(_e, tenant) => {
        setSelected(tenant);
        onChange(tenant);
      }}
      noOptionsText={input ? 'No tenant matches' : 'Type to search tenants'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};

export default TenantPicker;
